import React, { useEffect, useState } from "react";
import { Check, ArrowUpRight, Loader2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { createCheckout, listPlans } from "@/lib/api";
import { PlanBadge } from "@/components/StatusPill";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { BILLING } from "@/constants/testIds";

export default function Billing() {
  const { org } = useAuth();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [working, setWorking] = useState(null);

  useEffect(() => { listPlans().then((r) => setPlans(r.plans)); }, []);

  const upgrade = async (planId) => {
    setWorking(planId);
    try {
      const origin = window.location.origin;
      const { url } = await createCheckout(planId, origin);
      window.location.href = url;
    } catch (err) {
      toast({ title: "Could not start checkout", description: err?.response?.data?.detail, variant: "destructive" });
      setWorking(null);
    }
  };

  return (
    <>
      <PageHeader title="Billing" subtitle="Stewardship that scales with your mission." />
      <PageBody>
        {org && (
          <div data-testid={BILLING.status} className="border border-border bg-card rounded-md p-6 mb-8 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Current plan</div>
              <div className="mt-2 flex items-center gap-3">
                <PlanBadge plan={org.plan} />
                {org.plan_renews_at && (
                  <span className="text-xs text-muted-foreground mono">renews {org.plan_renews_at.slice(0, 10)}</span>
                )}
              </div>
            </div>
            <div className="text-xs text-muted-foreground max-w-md text-right">
              Pay-per-month, cancel anytime. All upgrades extend your plan by 30 days from purchase.
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-5">
          {plans.map((p) => {
            const isCurrent = org?.plan === p.id;
            const accent = p.id === "growth";
            return (
              <div
                key={p.id}
                data-testid={BILLING.planCard(p.id)}
                className={`relative border rounded-md p-6 bg-card ${accent ? "border-secondary" : "border-border"}`}
              >
                {accent && (
                  <div className="absolute top-0 right-6 -translate-y-1/2 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium">
                    Recommended
                  </div>
                )}
                <div className="serif text-2xl text-primary">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="serif text-4xl text-primary tabular mono">${p.price}</span>
                  <span className="text-xs text-muted-foreground">/ mo</span>
                </div>
                <p className="text-sm text-muted-foreground mt-3">{p.tagline}</p>
                <ul className="mt-5 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2"><Check size={14} className="mt-0.5 text-secondary shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    <div className="text-center text-xs uppercase tracking-wider text-muted-foreground border border-dashed border-border rounded-md py-2.5">
                      Current plan
                    </div>
                  ) : p.id === "community" ? (
                    <div className="text-center text-xs uppercase tracking-wider text-muted-foreground py-2.5">
                      Default for new orgs
                    </div>
                  ) : (
                    <button
                      data-testid={BILLING.upgradeBtn(p.id)}
                      onClick={() => upgrade(p.id)}
                      disabled={!!working}
                      className={`w-full inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60 ${
                        accent ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : "bg-primary text-primary-foreground hover:bg-primary/90"
                      }`}
                    >
                      {working === p.id ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
                      Upgrade to {p.name}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PageBody>
    </>
  );
}
